<script lang="ts">
    import ajax from "../ajax/ajax";
    import Alert from "../components/alert/Alert.svelte";
    import Button from "../components/buttons/Button.svelte";
    import IconButton from "../components/buttons/IconButton.svelte";
    import AddIcon from "../components/icons/AddIcon.svelte";
import CloseIcon from "../components/icons/CloseIcon.svelte";
    import CompareIcon from "../components/icons/CompareIcon.svelte";
    import DeleteCircleIcon from "../components/icons/DeleteCircleIcon.svelte";
    import DoneAllIcon from "../components/icons/DoneAllIcon.svelte";
    import DoneIcon from "../components/icons/DoneIcon.svelte";
    import PullIcon from "../components/icons/PullIcon.svelte";
    import PushIcon from "../components/icons/PushIcon.svelte";
    import UndoIcon from "../components/icons/UndoIcon.svelte";
    import WaitIcon from "../components/icons/WaitIcon.svelte";
    import Snackbar from "../components/snackbar/Snackbar.svelte";

    export let projectId: number;
    export let closeCallback: () => void;

    type TStatusResponse = {
        staged: string[];
        changed: string[];
        untracked: string[];
    };

    type TStatusResponseWithMessage = TStatusResponse & {
        message: string;
    };

    let status: undefined | TStatusResponse;

    let timeout: number | null = null;
    let errorMessage = null;

    let working = true;

    let diffToShow: string | null = null;

    function getErrorMessageFromAjax(
        xhr: XMLHttpRequest,
        fallback: string
    ): string {
        try {
            const returnValue = JSON.parse(xhr.responseText);
            if (returnValue) {
                return returnValue.errorMessage
                    ? returnValue.errorMessage
                    : fallback;
            }
        } catch {}

        return fallback;
    }

    ajax.get(
        `/projects/${projectId}/versionControl/status`,
        (response: TStatusResponse) => {
            status = response;
            working = false;
        },
        (s, c, xhr) => {
            working = false;
            if (null !== timeout) {
                clearTimeout(timeout);
            }
            errorMessage = getErrorMessageFromAjax(
                xhr,
                "Could not get version control status from server."
            );
            timeout = setTimeout(() => {
                errorMessage = null;
            }, 10000);
        }
    );

    function addToStage(files: string[]) {
        working = true;
        ajax.put(
            `/projects/${projectId}/versionControl/stage`,
            {
                files,
            },
            (response: TStatusResponse) => {
                status = response;
                working = false;
            },
            (s, c, xhr) => {
                working = false;
                if (null !== timeout) {
                    clearTimeout(timeout);
                }
                errorMessage = getErrorMessageFromAjax(
                    xhr,
                    "Could not add to stage."
                );
                timeout = setTimeout(() => {
                    errorMessage = null;
                }, 10000);
            }
        );
    }

    function showDiff(file: string) {
        working = true;
        ajax.post(
            `/projects/${projectId}/versionControl/diff`,
            {
                file,
            },
            (response: any) => {
                diffToShow = response.diff;
                working = false;
            },
            (s, c, xhr) => {
                working = false;
                if (null !== timeout) {
                    clearTimeout(timeout);
                }
                errorMessage = getErrorMessageFromAjax(
                    xhr,
                    "Could not get diff."
                );
                timeout = setTimeout(() => {
                    errorMessage = null;
                }, 10000);
            }
        );
    }

    function removeFromStage(files: string[]) {
        working = true;
        ajax.delete(
            `/projects/${projectId}/versionControl/stage`,
            (response: TStatusResponse) => {
                status = response;
                working = false;
            },
            (s, c, xhr) => {
                working = false;
                if (null !== timeout) {
                    clearTimeout(timeout);
                }
                errorMessage = getErrorMessageFromAjax(
                    xhr,
                    "Could not remove from stage."
                );
                timeout = setTimeout(() => {
                    errorMessage = null;
                }, 10000);
            },
            {
                files,
            }
        );
    }

    let commitAllMessage = "";
    let commitStagedMessage = "";

    function commit(message: string, all = false) {
        working = true;
        ajax.post(
            `/projects/${projectId}/versionControl/${
                all ? "" : "stage/"
            }commit`,
            {
                message,
            },
            (response: TStatusResponse) => {
                status = response;
                if (all) {
                    commitAllMessage = "";
                } else {
                    commitStagedMessage = "";
                }
                working = false;
            },
            (s, c, xhr) => {
                working = false;
                if (null !== timeout) {
                    clearTimeout(timeout);
                }
                errorMessage = getErrorMessageFromAjax(
                    xhr,
                    "Could not commit."
                );
                timeout = setTimeout(() => {
                    errorMessage = null;
                }, 10000);
            }
        );
    }

    let lastMessage: null | {
        type: "notice" | "success";
        message: string;
    } = null;
    let lastMessageTimeout: number | null = null;

    function pull() {
        working = true;
        ajax.get(
            `/projects/${projectId}/versionControl/pull`,
            (response: TStatusResponseWithMessage) => {
                status = {
                    staged: status.staged,
                    changed: status.changed,
                    untracked: status.untracked,
                };
                if (lastMessageTimeout) {
                    clearTimeout(lastMessageTimeout);
                }
                lastMessage = {
                    type: "notice",
                    message: response.message,
                };
                setTimeout(() => {
                    lastMessage = null;
                    lastMessageTimeout = null;
                }, 5000);
                working = false;
            },
            (s, c, xhr) => {
                working = false;
                if (null !== timeout) {
                    clearTimeout(timeout);
                }
                errorMessage = getErrorMessageFromAjax(
                    xhr,
                    "Could not commit."
                );
                timeout = setTimeout(() => {
                    errorMessage = null;
                }, 10000);
            }
        );
    }

    function push() {
        working = true;
        ajax.put(
            `/projects/${projectId}/versionControl/push`,
            {},
            (response: TStatusResponseWithMessage) => {
                status = {
                    staged: status.staged,
                    changed: status.changed,
                    untracked: status.untracked,
                };
                if (lastMessageTimeout) {
                    clearTimeout(lastMessageTimeout);
                }
                lastMessage = {
                    type: "success",
                    message: response.message,
                };
                setTimeout(() => {
                    lastMessage = null;
                    lastMessageTimeout = null;
                }, 5000);
                working = false;
            },
            (s, c, xhr) => {
                working = false;
                if (null !== timeout) {
                    clearTimeout(timeout);
                }
                errorMessage = getErrorMessageFromAjax(xhr, "Could not push.");
                timeout = setTimeout(() => {
                    errorMessage = null;
                }, 10000);
            }
        );
    }
</script>

{#if diffToShow}
    <div class="DiffOverlay">
        <IconButton icon={CloseIcon} title="Close" onClick={() => {
            diffToShow = null;
        }} />
        <div class="Diff">
            {@html diffToShow}
        </div>
    </div>
{/if}

<div class="VersionControlOverlay">
    <IconButton icon={CloseIcon} title="Close" onClick={() => {
        closeCallback();
    }} />
    {#if status}
        {#if working}
            <div class="Working">
                <WaitIcon width="40px" height="40px" />
            </div>
        {/if}
        {#if status.staged.length === 0 && status.changed.length === 0 && status.untracked.length === 0}
            No changes.
        {:else}
            <section>
                <div class="Commit">
                    <input
                        type="text"
                        placeholder="Commit message"
                        bind:value={commitAllMessage}
                    />
                    <Button
                        icon={DoneAllIcon}
                        label="Commit all"
                        color="success"
                        onclick={() => {
                            commit(commitAllMessage, true);
                        }}
                    />
                </div>
                <p>Stages all untracked files and changes and commits them.</p>
            </section>

            {#if status.staged.length > 0}
                <section>
                    <h2>Staged for commit</h2>
                    <div class="Commit">
                        <input
                            type="text"
                            placeholder="Commit message"
                            bind:value={commitStagedMessage}
                        />
                        <Button
                            icon={DoneIcon}
                            label="Commit"
                            color="success"
                            onclick={() => {
                                commit(commitStagedMessage, false);
                            }}
                        />
                    </div>
                    <div>
                        {#each status.staged as file}
                            <div>
                                {file}
                                <IconButton
                                    icon={DeleteCircleIcon}
                                    title="Unstage"
                                    onClick={() => {
                                        removeFromStage([file]);
                                    }}
                                />
                            </div>
                        {/each}
                    </div>
                </section>
            {/if}

            {#if status.changed.length > 0}
                <section>
                    <h2>Unstaged changes</h2>
                    <div>
                        {#each status.changed as file}
                            <div>
                                {file}
                                <IconButton
                                    icon={UndoIcon}
                                    title="Revert"
                                    onClick={() => {
                                        if (
                                            confirm("Revert selected changes?")
                                        ) {
                                        }
                                    }}
                                />
                                <IconButton
                                    icon={CompareIcon}
                                    title="Show diff"
                                    onClick={() => {
                                        showDiff(file);
                                    }}
                                />
                                <IconButton
                                    icon={AddIcon}
                                    title="Stage"
                                    onClick={() => {
                                        addToStage([file]);
                                    }}
                                />
                            </div>
                        {/each}
                    </div>
                </section>
            {/if}

            {#if status.untracked.length > 0}
                <section>
                    <h2>Untracked files</h2>
                    <div>
                        {#each status.untracked as file}
                            <div>
                                {file}
                                <IconButton
                                    icon={AddIcon}
                                    title="Add"
                                    onClick={() => {
                                        addToStage([file]);
                                    }}
                                />
                            </div>
                        {/each}
                    </div>
                </section>
            {/if}
        {/if}
        <section>
            <h2>Repository</h2>
            <Button
                icon={PullIcon}
                label="Pull"
                color="secondary"
                onclick={() => {
                    pull();
                }}
            />
            <Button
                icon={PushIcon}
                label="Push"
                color="primary"
                onclick={() => {
                    push();
                }}
            />
            {#if lastMessage}
                <Alert {...lastMessage} />
            {/if}
        </section>
    {:else}
        Loading...
    {/if}
</div>

{#if errorMessage}
    <Snackbar type="error" message={errorMessage} />
{/if}

<style type="text/scss">
    @import "../scss/config.scss";
    .VersionControlOverlay {
        position: fixed;
        z-index: 2;
        right: 0;
        top: 0;
        bottom: 0;
        height: 100vh;
        background: $color-white;
        padding: 20px;
        border-left: 2px solid $color-medium-grey;
        width: 75%;
        max-width: 500px;
        box-shadow: 0 0 140px 80px #e4e4e4;
    }
    .Working {
        position: fixed;
        z-index: 3;
        right: 0;
        top: 0;
        bottom: 0;
        height: 100vh;
        background: rgba($color-medium-grey, 0.5);
        padding: 20px;
        border-left: 2px solid $color-medium-grey;
        width: 75%;
        max-width: 500px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    section {
        border-bottom: 1px solid $color-light-to-medium-grey;
        padding: 20px 0;
    }
    h2 {
        margin: 0 0 15px 0;
        font-size: 18px;
    }
    p {
        font-size: 14px;
        font-style: italic;
        margin: 0;
    }
    .Commit {
        display: flex;
        margin-bottom: 15px;
        input {
            flex-grow: 1;
            margin: 0 10px 0 0;
        }
    }
    .DiffOverlay {
        position: fixed;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        height: 100vh;
        overflow: auto;
        width: 100%;
        z-index: 4;
        padding: 30px;
        background: $color-white;
        .Diff {
            font-family: 'Courier Prime', 'Courier New', Courier, monospace;
            max-width: 700px;
            margin: auto;
        }
    }
</style>
